import React, { FC } from 'react';
import { Checkbox, FormControlLabel, Grid, Typography } from '@rbx/ui';
import { Locale, useTranslation } from '@rbx/intl';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { forumBaseUrl } from '@modules/utilities/constants/baseUrl';
import feedbackGuidelinesLink from '../constants/feedbackGuidelinesLink';
import useShareFeedbackStyles from './ShareFeedback.styles';

interface ShareFeedbackControlOrGuidelinesProps {
  isPublicDevForumFeedbackAllowed: boolean;
  isFeedbackPublic: boolean;
  setIsFeedbackPublic: (val: boolean) => void;
}

const ShareFeedbackControlOrGuidelines: FC<
  React.PropsWithChildren<ShareFeedbackControlOrGuidelinesProps>
> = ({ isPublicDevForumFeedbackAllowed, isFeedbackPublic, setIsFeedbackPublic }) => {
  const { urlLocale } = useCurrentLocale();
  const { translate, translateHTML } = useTranslation();
  const {
    classes: { feedbackPublicCheckbox, feedbackPublicCheckboxLabel, viewGuidelines },
  } = useShareFeedbackStyles();
  const showCheckboxControl = isPublicDevForumFeedbackAllowed;

  return (
    <React.Fragment>
      {showCheckboxControl && (
        <Grid item alignItems='flex-start'>
          <FormControlLabel
            className={feedbackPublicCheckbox}
            checked={isFeedbackPublic}
            onChange={() => setIsFeedbackPublic(!isFeedbackPublic)}
            control={<Checkbox color='secondary' size='medium' />}
            labelPlacement='end'
            label={
              <Typography
                className={feedbackPublicCheckboxLabel}
                variant='body1'
                color='secondary'
                component='p'>
                {translateHTML('Message.MakeFeedbackPublic', [
                  {
                    opening: 'devForumLinkStart',
                    closing: 'devForumLinkEnd',
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content: (chunk) => (
                      <DynamicallyRouted.Link target='_blank' href={forumBaseUrl}>
                        {chunk}
                      </DynamicallyRouted.Link>
                    ),
                  },
                ])}
              </Typography>
            }
          />
        </Grid>
      )}
      <Grid
        item
        className={viewGuidelines}
        marginTop={1.5}
        marginBottom={isPublicDevForumFeedbackAllowed ? 2 : 0.5}>
        {isFeedbackPublic && urlLocale !== Locale.English && (
          <Typography variant='body1' paragraph>
            {translate('Message.PublicForumPostEnglishPreferred')}
          </Typography>
        )}
        <Typography variant='body1'>
          {isFeedbackPublic && translate('Message.PublicForumPostConfirmation')}{' '}
          {translateHTML('Message.ViewOurFeedbackGuidelinesHere', [
            {
              opening: 'guidelinesLinkStart',
              closing: 'guidelinesLinkEnd',
              // eslint-disable-next-line react/no-unstable-nested-components
              content: (chunk) => (
                <DynamicallyRouted.Link target='_blank' href={feedbackGuidelinesLink}>
                  {chunk}
                </DynamicallyRouted.Link>
              ),
            },
          ])}
        </Typography>
      </Grid>
    </React.Fragment>
  );
};

export default ShareFeedbackControlOrGuidelines;
